import { useMemo } from 'react';
import { useUsageStats } from './useUsageStats';
import type { SubscriptionTier, ActionType } from '@/types';

export interface LimitCheckResult {
  canPerform: boolean;
  isNearLimit: boolean;
  remainingCount: number;
  limitCount: number;
  percentage: number;
}

export interface UseLimitCheckOptions {
  actionType: ActionType;
  subscriptionTier?: SubscriptionTier;
}

const TIER_LIMITS = {
  FREE: { purchases: 5, listings: 5, items: 20 },
  ACTIVE: { purchases: Infinity, listings: Infinity, items: Infinity }
} as const;

export function useLimitCheck({ actionType, subscriptionTier }: UseLimitCheckOptions): LimitCheckResult {
  const { data: usageData, isLoading } = useUsageStats();

  return useMemo(() => {
    if (isLoading || !usageData?.data || !subscriptionTier) {
      return {
        canPerform: false,
        isNearLimit: false,
        remainingCount: 0,
        limitCount: 0,
        percentage: 0
      };
    }

    const currentUsage = usageData.data.current;
    const limits = TIER_LIMITS[subscriptionTier];
    const limitCount = limits[actionType];
    const currentCount = currentUsage[actionType] || 0;

    if (limitCount === Infinity) {
      return {
        canPerform: true,
        isNearLimit: false,
        remainingCount: Infinity,
        limitCount: Infinity,
        percentage: 0
      };
    }

    const remainingCount = Math.max(0, limitCount - currentCount);
    const canPerform = remainingCount > 0;
    const percentage = Math.min(100, (currentCount / limitCount) * 100);
    const isNearLimit = percentage >= 80;

    return {
      canPerform,
      isNearLimit,
      remainingCount,
      limitCount,
      percentage
    };
  }, [usageData, subscriptionTier, actionType, isLoading]);
}